import { Lightning, Registry, Router, Settings } from '@lightningjs/sdk'

import VideoPlayer from '.'
import { getColor, grey300, grey50, grey960, transparent } from '../../constants/colors'
import { UniNeueBold } from '../../constants/fonts'
import { Images } from '../../constants/paths'
import { HOME } from '../../constants/routes'
import { PLAYBACK_SIGNALS, PLAYER_CONTROLS_SIGNALS } from '../../constants/signals'
import { KEY_CODE } from '../../constants/videoPlayer'
import { AppConfig } from '../../services/appConfig'
import { RenderableComponentMetadataEnum } from '../../types/flo-enums'
import { TvCarouselContainerModel } from '../../types/flo-experience'
import { getAsset } from '../../utils/image'
import { debounce } from '../../utils/misc'
import { calculateRenderInfo } from '../../utils/text'
import { HorizontalPointerWidth } from '../NavigationPointers/NavigationPointers'
import ControlButtons, { PlayerControlButton } from './playerButtons'
import Ranges from './ranges'
import UpNextCarousel from './upNextCarousel'

export enum VideoControlStates {
  Hidden = 'Hidden',
  ControlButtons = 'ControlButtons',
  Ranges = 'Ranges',
  Carousel = 'Carousel',
}

const DEFAULT_CAROUSELS_OFFSET = 115

const CAROUSEL_FOCUS_VOD_POS = 400
const CAROUSEL_FOCUS_LIVE_POS = 400
const CAROUSEL_HIDDEN_POS = 880
const BACKGROUND_INITIAL_POSITION = 560
const SLIDE_ANIM_TIME = 0.25

const DEFAULT_SEEK_JUMP = 10
const SCREEN_WIDTH = 1920

export default class VideoControl extends Lightning.Component {
  private _isControlsContainerVisible = false
  private _controlsTimerId = -1
  private _authDeeplinked = false
  private _animControlsFadeIn: Lightning.Animation
  private _animControlsFadeOut: Lightning.Animation
  private _animPlayStateFadeIn: Lightning.Animation
  private _animPlayStateFadeOut: Lightning.Animation
  private _animationSlideUp: Lightning.Animation
  private _animationSlideDown: Lightning.Animation
  private _keyDownTime: number
  private _isScrubMode = false
  private _isScrubbing = false
  private _scrubSpeed = 30 // Initial speed
  private _scrubDirection = 1
  private _video: HTMLVideoElement
  private _mouseMoveCallback: Function

  static _template() {
    return {
      h: 1080,
      w: SCREEN_WIDTH,
      color: transparent,
      rect: true,
      PlayStateAnim: {
        w: SCREEN_WIDTH,
        h: 1080,
        alpha: 0,
        zIndex: 2,
        flex: { alignItems: 'center', justifyContent: 'center' },
        Button: {
          w: 200,
          h: 200,
          rect: true,
          color: getColor(grey960),
          alpha: 0.4,
          flex: { direction: 'column', alignItems: 'center', justifyContent: 'center' },
          shader: {
            type: Lightning.shaders.RoundedRectangle,
            radius: 100,
          },
          PlayStateImage: {
            w: 48,
            h: 48,
            shader: null,
            texture: Lightning.Tools.getSvgTexture(getAsset(Images.PLAYER_PLAY), 48, 48),
          },
        },
      },
      Container: {
        x: 0,
        y: 0,
        h: 1080,
        w: SCREEN_WIDTH,
        alpha: 0,
        Background: {
          w: SCREEN_WIDTH,
          h: 1000,
          x: 0,
          y: 560,
          flex: { direction: 'column' },

          children: [
            {
              h: 520,
              texture: Lightning.Tools.getSvgTexture(
                getAsset(Images.PLAYER_CONTROLS_GRADIENT),
                SCREEN_WIDTH,
                520,
              ),
            },
            {
              h: 480,
              rect: true,
              color: getColor(grey960),
            },
          ],
        },
        InnerContainer: {
          // This value is by measuring instead from expo-av source,
          // which only said wrap_content.
          h: 240,
          w: SCREEN_WIDTH,
          // x: bottom, y: right
          mount: 1,
          x: SCREEN_WIDTH,
          y: 880,
          rect: true,
          color: transparent,
          flex: { direction: 'column', alignItems: 'center' },
          ControlsContainer: {
            w: 1728,
            h: 116,
            flexItem: {
              marginBottom: 80,
            },
            flex: { direction: 'row', alignItems: 'center' },
            Metadata: {
              flexItem: { marginTop: 160 },
              w: 716,
              flex: { direction: 'column' },
              Title: {
                text: {
                  text: '',
                  textColor: getColor(grey50),
                  fontSize: 32,
                  fontFace: UniNeueBold,
                  lineHeight: 36,
                  maxLines: 2,
                  maxLinesSuffix: '...',
                  wordWrapWidth: 716,
                },
              },
              Subtitle: {
                flexItem: { marginTop: -5 },
                h: 36,
                text: {
                  text: '',
                  textColor: getColor(grey300),
                  fontSize: 28,
                  lineHeight: 28,
                },
              },
            },
            ControlButtons: {
              w: (w) => w,
              flexItem: { marginTop: 90 },
              type: ControlButtons,
              signals: PLAYBACK_SIGNALS,
              passSignals: PLAYBACK_SIGNALS,
            },
          },
          Ranges: {
            w: 1728,
            type: Ranges,
            signals: {
              ...PLAYBACK_SIGNALS,
              resumeFromTrickplay: 'resumeFromTrickplay',
              scrubFromClick: 'scrubFromClick',
              scrubFromHover: 'scrubFromHover',
            },
            passSignals: PLAYBACK_SIGNALS,
          },
          UpNextCarouselContainer: {
            x: 100,
            w: (w) => w,
            h: (h) => h,
            UpNextCarousel: {
              type: UpNextCarousel,
              withSpotlight: true,
              offset: DEFAULT_CAROUSELS_OFFSET,
            },
          },
        },
      },
    }
  }

  set isLiveStream(v) {
    this.ranges.isLiveStream = v
  }

  get isLiveStream() {
    return this.ranges.isLiveStream
  }

  set authDeeplinked(v) {
    this._authDeeplinked = v
  }

  get uiTimeout() {
    return Settings.get('app', 'video.uiHideTimeout', 10000)
  }

  get container() {
    return this.tag('Container')
  }

  get innerContainer() {
    return this.tag('InnerContainer')
  }

  get upNextCarouselContainer() {
    return this.tag('UpNextCarouselContainer')
  }

  get controlButtons() {
    return this.tag('ControlButtons') as ControlButtons
  }

  get ranges() {
    return this.tag('Ranges') as Ranges
  }

  get upNextCarousel() {
    return this.tag('UpNextCarousel') as UpNextCarousel
  }

  set upNextCarouselVisible(v: boolean) {
    if ((this.upNextCarousel?.data as TvCarouselContainerModel[])?.[0]?.items?.length > 0) {
      this.upNextCarousel.visible = v
    }
  }

  get title() {
    return this.tag('Title')
  }

  get subtitle() {
    return this.tag('Subtitle')
  }

  get playStateImage() {
    return this.tag('PlayStateImage')
  }

  get playerState() {
    return this.controlButtons.playerState
  }

  set playerState(value) {
    this.controlButtons.playerState = value

    if (this._isControlsContainerVisible) {
      if (this.controlButtons.playerState) {
        this.hideControls()
      } else {
        Registry.clearTimeout(this._controlsTimerId)
      }
    }
  }

  get videoPlayer() {
    return this.parent as VideoPlayer
  }

  get currentTime() {
    return this.ranges.currentTime
  }

  set currentTime(value) {
    this.ranges.currentTime = value
  }

  get duration() {
    return this.ranges.duration
  }

  set duration(value) {
    this.ranges.duration = value
  }

  set timeLimitText(value) {
    this.ranges.timeLimitText = value
  }

  set metadata(value) {
    this.title.patch({ text: value?.title })
    this.subtitle.patch({ text: value?.subtitle })

    const renderInfo = calculateRenderInfo(this.title.text, value?.title)
    this.tag('Metadata').patch({
      h: renderInfo?.lines?.length > 1 ? 180 : 100,
    })

    const data = value.data
    const type = data?.content_metadata?.type

    if (data?.web_game_center_url) {
      this.controlButtons.setButtonActive(PlayerControlButton.Scores)
    }
    if (data?.web_brackets_url) {
      this.controlButtons.setButtonActive(PlayerControlButton.Brackets)
    }
    if (type !== RenderableComponentMetadataEnum.Vod) {
      this.controlButtons.setButtonActive(PlayerControlButton.Feedback)
    }
  }

  get isScrubMode() {
    return this._isScrubMode
  }

  get isScrubbing() {
    return this._isScrubbing
  }

  static _states() {
    return [
      class Hidden extends this {
        $enter() {
          if (this._isControlsContainerVisible) {
            this._animControlsFadeIn.stop()
            this._animControlsFadeOut.replay()
          }

          this.fireAncestors('$toggleControlsVisibility', false)
        }
        $exit() {
          this._isControlsContainerVisible = true
          this._animControlsFadeOut.stop()
          this._animControlsFadeIn.replay()
        }
      },
      class ControlButtons extends this {
        $enter() {
          this.fireAncestors('$toggleControlsVisibility', true)
        }

        _getFocused(): Lightning.Element {
          return this.controlButtons
        }

        _handleKey({ keyCode }: { keyCode: any }): boolean {
          if (keyCode === KEY_CODE.DOWN) {
            this._setState(VideoControlStates.Ranges)
            return true
          }
          return false
        }

        _handleBack() {
          this.hideControls()
          return true
        }
      },
      class Ranges extends this {
        $enter() {
          this.fireAncestors('$toggleControlsVisibility', true)
        }

        $exit() {
          this.ranges.preventCurrentTimeUpdate = false
        }

        _getFocused() {
          return this.ranges
        }

        _handleUp() {
          if (this._isScrubMode) {
            return true
          }

          if (!this._isScrubbing) {
            this.cancelScrubMode()
            this._setState(VideoControlStates.ControlButtons)
          }

          return true
        }

        _handleDown() {
          const hasUpNextItems =
            (this.upNextCarousel.data as TvCarouselContainerModel[])?.[0]?.items?.length > 0

          if (hasUpNextItems && !this.isScrubMode && !this.isScrubbing) {
            this._setState(VideoControlStates.Carousel)
          }

          return true
        }

        _handleKey({ keyCode }: { keyCode: any }): boolean {
          if (keyCode === KEY_CODE.MEDIA_STOP && (this._isScrubbing || this._isScrubMode)) {
            this.stop()
          } else if (
            keyCode === KEY_CODE.PLAY_PAUSE ||
            keyCode === KEY_CODE.MEDIA_PLAY_PAUSE ||
            keyCode === KEY_CODE.MEDIA_PLAY
          ) {
            this.resumePlayback()
          } else if (keyCode === KEY_CODE.MEDIA_FF) {
            this.onMediaFFW()
          } else if (keyCode === KEY_CODE.MEDIA_RW) {
            this.onMediaRW()
          } else {
            if (this._isScrubbing || this._isScrubMode) {
              return true
            }

            this.controlButtons._handleKey({
              keyCode,
            })
          }
        }

        _handleEnter() {
          this.fireAncestors('$toggleControlsVisibility', true)
          this.resumePlayback()
        }

        _handleBack() {
          if (this._isScrubMode) {
            this._isScrubMode = false
            this._isScrubbing = false
            this.hideControls()
            this.toggleControlsForScrubbing(true)
            this.hideScrubbingTime()
            this.hideSpeedIndicators()
            this.ranges.resetSeekJumpAnims()
            this._keyDownTime = null
            this.fireAncestors('$toggleControlsVisibility', false)

            if (!this.playerState) {
              this.videoPlayer.player.getHls().startLoad()
              this.controlButtons.playPause()
            }
          }

          this._setState(VideoControlStates.Hidden)

          return true
        }

        _handleLeft() {
          this.fireAncestors('$toggleControlsVisibility', false)

          if (this._isScrubbing) {
            return
          }

          this.startScrubbing(false)
        }

        _handleLeftRelease() {
          this.fireAncestors('$toggleControlsVisibility', false)

          if (this._scrubSpeed === 0) {
            if (this.playerState) {
              this.videoPlayer.player.getHls().startLoad()
              this.controlButtons.playPause()
            }

            this.showScrubbingTime()
            this.ranges.showSeekJumpRW()
            this.updateScrubUIBackwards()
          }

          this._isScrubbing = false
          this._keyDownTime = null
          this.hideSpeedIndicators()
        }

        _handleRight() {
          this.fireAncestors('$toggleControlsVisibility', false)

          if (this._isScrubbing) {
            return
          }

          this.startScrubbing(true)
        }

        _handleRightRelease() {
          this.fireAncestors('$toggleControlsVisibility', false)

          if (this._scrubSpeed === 0) {
            if (this.playerState) {
              this.videoPlayer.player.getHls().startLoad()
              this.controlButtons.playPause()
            }

            this.showScrubbingTime()
            this.ranges.showSeekJumpFF()
            this.updateScrubUIForward()
          }

          this._isScrubbing = false
          this._keyDownTime = null
          this.hideSpeedIndicators()
        }

        // This will get called from the Ranges component only, by clicking on the progress bar
        scrubFromClick(scrubTo, automaticallyResume = false) {
          const isForward = scrubTo > this.ranges.progressBar.progress
          this.ranges.progressBar.progress = scrubTo
          // The progress bar intermediate state when scrubbing was getting overwritten by the onTimeUpdate currentTime update.
          // This flag prevents such thing from happening, which does not ocurr when scrubbing using the keyboard
          this.ranges.preventCurrentTimeUpdate = true
          this.startScrubbing(isForward)

          if (isForward) {
            this.ranges.progressBar.progress -= DEFAULT_SEEK_JUMP
            this._handleRightRelease()
          } else {
            this.ranges.progressBar.progress += DEFAULT_SEEK_JUMP
            this._handleLeftRelease()
          }

          if (automaticallyResume) {
            this.resumeFromTrickplay()

            Registry.setTimeout(() => {
              this.ranges.seekJumpRW.alpha = 0
              this.ranges.seekJumpFF.alpha = 0
            }, 100)
          } else {
            this.fireAncestors('$togglePointerBackButtonLabel', true)
          }
        }

        // This will get called from the Ranges component only, by clicking on the trickplay thumbnail
        resumeFromTrickplay() {
          super.resumePlayback()
        }
      },
      class Carousel extends this {
        $enter() {
          // When focus is on carousel don't dismiss the controls on timeout
          Registry.clearTimeout(this._controlsTimerId)

          let startY = CAROUSEL_HIDDEN_POS
          let bgStartY = BACKGROUND_INITIAL_POSITION
          if (this._animationSlideDown) {
            startY = this.innerContainer.y
            this._animationSlideDown.stopNow()
          }

          this._animationSlideUp = this.animation({
            duration: SLIDE_ANIM_TIME,
            actions: [
              {
                t: 'InnerContainer',
                p: 'y',
                v: {
                  0: { v: startY },
                  1: { v: this.isLiveStream ? CAROUSEL_FOCUS_LIVE_POS : CAROUSEL_FOCUS_VOD_POS },
                },
              },
              {
                t: 'Background',
                p: 'y',
                v: {
                  0: { v: bgStartY },
                  1: {
                    v:
                      (this.isLiveStream ? CAROUSEL_FOCUS_LIVE_POS : CAROUSEL_FOCUS_VOD_POS) -
                      this.innerContainer.h,
                  },
                },
              },
            ],
          })

          this._animationSlideUp.replay()
          this.fireAncestors('$upNextCarouselFocused', true)
          this.checkNavigationPointersVisibility(true)
        }

        $exit() {
          this.restartControlsTimer()
          let startY = this.isLiveStream ? CAROUSEL_FOCUS_LIVE_POS : CAROUSEL_FOCUS_VOD_POS
          if (this._animationSlideUp) {
            startY = this.innerContainer.y
            this._animationSlideUp.stopNow()
          }
          this._animationSlideDown = this.animation({
            duration: SLIDE_ANIM_TIME,
            actions: [
              {
                t: 'InnerContainer',
                p: 'y',
                v: { 0: { v: startY }, 1: { v: CAROUSEL_HIDDEN_POS } },
              },
              {
                t: 'Background',
                p: 'y',
                v: { 0: { v: startY }, 1: { v: BACKGROUND_INITIAL_POSITION } },
              },
            ],
          })

          this._animationSlideDown.replay()
          this.fireAncestors('$upNextCarouselFocused', false)
          this.checkNavigationPointersVisibility(false)
        }

        _getFocused() {
          return this.upNextCarousel
        }

        _handleUp() {
          this._setState(VideoControlStates.Ranges)
          return true
        }

        _handleKey({ keyCode }: { keyCode: any }): boolean {
          this.restartControlsTimer()

          if (keyCode === KEY_CODE.MEDIA_STOP) {
            this.stop()
          } else if (
            keyCode === KEY_CODE.PLAY_PAUSE ||
            keyCode === KEY_CODE.MEDIA_PLAY_PAUSE ||
            keyCode === KEY_CODE.MEDIA_PLAY ||
            keyCode === KEY_CODE.MEDIA_PAUSE
          ) {
            this.controlButtons._handleKey({
              keyCode,
            })
          } else if (keyCode === KEY_CODE.MEDIA_FF) {
            this.jumpForward()
          } else if (keyCode === KEY_CODE.MEDIA_RW) {
            this.jumpBackward()
          }

          return false
        }

        _handleBack() {
          this.hideControls()
          return true
        }
      },
    ]
  }

  _init() {
    this._animControlsFadeIn = this.animation({
      duration: 0.3,
      actions: [
        {
          t: 'Container',
          p: 'alpha',
          v: { 0: { v: 0 }, 1: { v: 1 } },
        },
      ],
    })

    this._animControlsFadeOut = this.animation({
      duration: 0.3,
      actions: [
        {
          t: 'Container',
          p: 'alpha',
          v: { 0: { v: 1 }, 1: { v: 0 } },
        },
      ],
    })

    this._animPlayStateFadeIn = this.animation({
      duration: 0.3,
      actions: [
        {
          t: 'PlayStateAnim',
          p: 'alpha',
          v: { 0: { v: 0 }, 1: { v: 1 } },
        },
      ],
    })

    this._animPlayStateFadeIn.on('finish', () => {
      this._animPlayStateFadeOut.replay()
    })

    this._animPlayStateFadeOut = this.animation({
      duration: 0.3,
      actions: [
        {
          t: 'PlayStateAnim',
          p: 'alpha',
          v: { 0: { v: 1 }, 1: { v: 0 } },
        },
      ],
    })

    this.attachMouseMoveEvent()

    this._setState(VideoControlStates.Hidden)
    this._isControlsContainerVisible = false
  }

  _inactive() {
    Registry.clearTimeout(this._controlsTimerId)
    Registry.removeEventListener(window, 'mousemove', this._mouseMoveCallback)
  }

  _getFocused() {
    return this.tag(this.state)
  }

  _handleKey({ keyCode }) {
    // Make sure we reset the ui timer each time there is an input from the user
    this.restartControlsTimer()

    switch (keyCode) {
      case KEY_CODE.LEFT:
      case KEY_CODE.RIGHT:
        this._setState(VideoControlStates.Ranges)
        this.startScrubbing(keyCode === KEY_CODE.RIGHT)
        return true

      case KEY_CODE.DOWN:
      case KEY_CODE.UP:
        this._setState(
          this.state === VideoControlStates.Hidden
            ? VideoControlStates.Ranges
            : VideoControlStates.ControlButtons,
        )
        return true

      case KEY_CODE.OK:
        if (this._getState() === VideoControlStates.Hidden) {
          this.resumePlayback()
          // Only jump to another state if the player just got paused
          // If the player started playing we just resume playback and don't show controls
          if (!this.playerState) {
            this._setState(VideoControlStates.Ranges)
          }

          return true
        }

        return false

      case KEY_CODE.FF:
      case KEY_CODE.RW:
      case KEY_CODE.PLAY_PAUSE:
      case KEY_CODE.MEDIA_FF:
      case KEY_CODE.MEDIA_RW:
      case KEY_CODE.MEDIA_PLAY_PAUSE:
      case KEY_CODE.MEDIA_PLAY:
      case KEY_CODE.MEDIA_PAUSE:
        this.controlButtons._handleKey({ keyCode })
        if (!this.playerState) {
          this._setState(VideoControlStates.Ranges)
        }

        break

      case KEY_CODE.MEDIA_STOP:
        this.stop()
        break

      default:
        return false
    }
  }

  updatePlayStateImage() {
    this.playStateImage.patch({
      texture: Lightning.Tools.getSvgTexture(
        getAsset(this.playerState ? Images.PLAYER_PLAY : Images.PLAYER_PAUSE),
        48,
        48,
      ),
      flexItem: { marginLeft: this.playerState ? 8 : 0 },
    })
  }

  resumePlayback() {
    const wasScrubbing = this._isScrubMode

    if (!this._isScrubbing && this._isScrubMode) {
      this._isScrubMode = false
      this.hideControls()
      this.toggleControlsForScrubbing(true)
      this.hideScrubbingTime()

      this.signal(PLAYER_CONTROLS_SIGNALS.seek, this.ranges.progressBar.progress)

      this._setState(VideoControlStates.Hidden)
    }

    this.videoPlayer.player.getHls().startLoad()
    this.controlButtons.playPause()

    // The function call above will trigger a play but we need to stop its animation if it's coming from scrubbing/seeking
    if (wasScrubbing) {
      this.stopPlayStateAnims()
    }

    if (this.playerState) {
      this._setState(VideoControlStates.Hidden)
    } else {
      Registry.clearTimeout(this._controlsTimerId)
    }

    this.fireAncestors('$togglePointerBackButtonLabel', false)
  }

  updateScrubUIBackwards() {
    let newTime = this.ranges.progressBar.progress - DEFAULT_SEEK_JUMP
    this.updateScrubUI(newTime)
  }

  // Will get called if the RW media key on the remote is pressed while either focus is on the playback buttons or when the player controls are hidden
  jumpBackward() {
    this._setState(VideoControlStates.Ranges)
    this.onMediaRW()
  }

  onMediaRW() {
    // These need to go in succession, otherwise, the rewinding won't stop!
    this.ranges.preventCurrentTimeUpdate = true
    ;(this as any)._handleLeft()
    ;(this as any)._handleLeftRelease()

    Registry.setTimeout(() => {
      // Needed, otherwise, the currentTime of the progress bar will keep updating and override the scrubbing progress, and pausing is not inmmediate
      this.ranges.preventCurrentTimeUpdate = false
    }, 100)
  }

  updateScrubUIForward() {
    let newTime = this.ranges.progressBar.progress + DEFAULT_SEEK_JUMP
    this.updateScrubUI(newTime)
  }

  // Will get called if the FFW media key on the remote is pressed while either focus is on the playback buttons or when the player controls are hidden
  jumpForward() {
    this._setState(VideoControlStates.Ranges)
    this.onMediaFFW()
  }

  onMediaFFW() {
    // These need to go in succession, otherwise, the ffwing won't stop!
    this.ranges.preventCurrentTimeUpdate = true
    ;(this as any)._handleRight()
    ;(this as any)._handleRightRelease()

    Registry.setTimeout(() => {
      // Needed, otherwise, the currentTime of the progress bar will keep updating and override the scrubbing progress, and pausing is not inmmediate
      this.ranges.preventCurrentTimeUpdate = false
    }, 100)
  }

  play() {
    this.signal(PLAYBACK_SIGNALS.play)
    this.updateAndPlayStateAnims()
  }

  pause() {
    if (this._controlsTimerId) {
      Registry.clearTimeout(this._controlsTimerId)
    }
    this.signal(PLAYBACK_SIGNALS.pause)
    this.updateAndPlayStateAnims()
  }

  stop() {
    this.signal(PLAYBACK_SIGNALS.stop)
  }

  updateAndPlayStateAnims() {
    this.updatePlayStateImage()
    this._animPlayStateFadeOut.stop()
    this._animPlayStateFadeIn.replay()
  }

  stopPlayStateAnims() {
    this._animPlayStateFadeIn.stop()
    this._animPlayStateFadeOut.stop()
  }

  startScrubbing(isForward) {
    Registry.clearTimeout(this._controlsTimerId)

    this.toggleControlsForScrubbing(false)

    this.showScrubbingTime()

    this._isScrubMode = true
    this._isScrubbing = true
    this._scrubSpeed = 0
    this._scrubDirection = isForward

    if (!this._video) {
      this._video = this.videoPlayer.videoElement
    }

    const indicators = isForward ? this.ranges.indicatorsFF : this.ranges.indicatorsRW
    indicators.forEach((indicator, index) => {
      indicator.alpha = index < 1 ? 1 : 0.3
    })

    if (!this._keyDownTime) {
      if (this.playerState) {
        this.videoPlayer.player.getHls().stopLoad()
        this.controlButtons.playPause()
        // The function call above will trigger a play but we need to stop its animation as it's coming from scrubbing/seeking
        this.stopPlayStateAnims()
      }

      this._keyDownTime = Date.now()

      this.updateScrubSpeed(isForward)
    }
  }

  updateScrubSpeed(isForward: boolean) {
    if (!this._isScrubbing) {
      return
    }

    let pressDuration = (Date.now() - this._keyDownTime) / 1000 // Convert milliseconds to seconds
    if (pressDuration < 1) {
      requestAnimationFrame(() => this.updateScrubSpeed(isForward))
      return
    }

    let seekSpeed = 480
    if (pressDuration < 2) {
      seekSpeed = 30
    } else if (pressDuration < 4) {
      seekSpeed = 120
    }
    this._scrubSpeed = seekSpeed
    this.updateSpeedIndicators(isForward)

    let seekDelta = this._scrubDirection ? seekSpeed / 60 : -seekSpeed / 60
    let newTime = this.ranges.progressBar.progress + seekDelta

    if (this.isLiveStream) {
      let liveThreshold = this._video.seekable.end(0)
      let minSeekTime = this._video.seekable.start(0)

      if (newTime > liveThreshold) newTime = liveThreshold // Prevent skipping past live
      if (newTime < minSeekTime) newTime = minSeekTime // Prevent rewinding too far
    }

    this.updateScrubUI(newTime)

    requestAnimationFrame(() => this.updateScrubSpeed(isForward))
  }

  updateScrubUI(newTime: number) {
    this.ranges.progressBar.visible = false
    this.ranges.scrubInfoContainer.visible = false

    if (newTime > this.duration) {
      newTime = this.duration
    } else if (newTime < 0) {
      newTime = 0
    }

    this.ranges.progressBar.progress = newTime
    this.ranges.scrubInfoContainer.x = this.ranges.progressBar.thumb.finalX
    this.ranges.timeScrubText = this.isLiveStream
      ? this.ranges.progressBar.progress - this.duration
      : newTime

    this.ranges.scrubTrickplay.updateThumbnail(newTime)

    // Adjust trickplay position to be fully visible
    const scrubInfoContainer = this.ranges.scrubInfoContainer
    if (scrubInfoContainer.x < scrubInfoContainer.w / 2) {
      scrubInfoContainer.patch({
        x: scrubInfoContainer.w / 2,
      })
    } else if (scrubInfoContainer.x > this.ranges.w - scrubInfoContainer.w / 2) {
      scrubInfoContainer.patch({
        x: this.ranges.w - scrubInfoContainer.w / 2,
      })
    }

    this.ranges.scrubInfoContainer.visible = true
    this.ranges.progressBar.visible = true
  }

  updateSpeedIndicators(isForward) {
    const opaqueCount = this._scrubSpeed <= 30 ? 1 : this._scrubSpeed <= 120 ? 2 : 3

    const indicators = isForward ? this.ranges.indicatorsFF : this.ranges.indicatorsRW
    indicators.forEach((indicator, index) => {
      indicator.setSmooth('alpha', index < opaqueCount ? 1 : 0.3, { duration: 0.1 })
    })

    if (isForward) {
      this.ranges.indicatorRW.alpha = 0
      this.ranges.indicatorFF.alpha = 1
    } else {
      this.ranges.indicatorRW.alpha = 1
      this.ranges.indicatorFF.alpha = 0
    }
  }

  hideSpeedIndicators() {
    this.ranges.indicatorRW.alpha = 0
    this.ranges.indicatorFF.alpha = 0
  }

  toggleControlsForScrubbing(toggle: boolean) {
    this.ranges.times.visible = toggle || this.isLiveStream
    this.title.visible = toggle
    this.subtitle.visible = toggle
    this.controlButtons.visible = toggle
    this.upNextCarouselVisible = toggle
    this.ranges.timeLimitVisible = toggle
    this.ranges.scrubInfoContainer.collision = !toggle
  }

  hideScrubbingTime() {
    this.ranges.scrubInfoContainer.visible = false
    this.ranges.scrubTime.visible = false
    this.ranges.scrubTrickplay.visible = false
  }

  cancelScrubMode() {
    this._isScrubMode = false
    this.toggleControlsForScrubbing(true)
    this.hideScrubbingTime()

    if (!this._video) {
      this._video = this.videoPlayer.videoElement
    }
    this.ranges.progressBar.progress = this._video.currentTime
  }

  preloadScrubTrickplay(initialTime) {
    const trickplay = this.videoPlayer.player.getTrickplayInfo() as TrickplayInfo
    if (trickplay?.thumbnails?.length && !this.ranges.scrubTrickplay.isTrickplayPreloaded) {
      this.ranges.scrubTrickplay.setTrickplayInfo(trickplay)
      this.ranges.scrubTrickplay.updateThumbnail(initialTime)
    }
  }

  showScrubbingTime() {
    this.ranges.scrubTime.visible = true
    const trickplay = this.videoPlayer.player.getTrickplayInfo() as TrickplayInfo
    if (trickplay?.thumbnails?.length) {
      this.ranges.scrubTrickplay.visible = true
      this.ranges.scrubTrickplay.setTrickplayInfo(trickplay)
    }
  }

  restartControlsTimer() {
    Registry.clearTimeout(this._controlsTimerId)
    this._controlsTimerId = null
    if (this.playerState) {
      this._controlsTimerId = Registry.setTimeout(() => {
        this._setState(VideoControlStates.Hidden)
        this._isControlsContainerVisible = false
      }, this.uiTimeout)
    }
  }

  hideControls() {
    if (this._isControlsContainerVisible) {
      this._animControlsFadeIn.stop()
      this._animControlsFadeOut.replay()
      this._isControlsContainerVisible = false
      this._setState(VideoControlStates.Hidden)
      Registry.clearTimeout(this._controlsTimerId)
    }
  }

  showControls() {
    if (!this._isControlsContainerVisible) {
      this._animControlsFadeOut.stop()
      this._animControlsFadeIn.replay()
      this._isControlsContainerVisible = true
      this._setState(VideoControlStates.Ranges)
      this.restartControlsTimer()
    }
  }

  showControlsOnMouseMove() {
    if (!this._isControlsContainerVisible) {
      const widgets = Router.getActivePage().widgets
      const isModalOpen = Object.values(widgets).some((widget: any) => {
        return widget.ref !== 'Menu' && widget.ref !== 'Loading' && widget.visible
      })

      if (!isModalOpen) {
        this.showControls()
      }
    }
  }

  goBack() {
    this.videoPlayer.close()
    if (Router.getHistory().length === 0 || this._authDeeplinked) {
      Router.navigate(HOME, false)
    } else {
      Router.back()
    }
  }

  attachMouseMoveEvent() {
    this._mouseMoveCallback = debounce(this.showControlsOnMouseMove.bind(this), 50)
    Registry.addEventListener(window, 'mousemove', this._mouseMoveCallback)
  }

  $updateFocusByHover(section) {
    if (
      this.state === VideoControlStates.Carousel ||
      (section.ref !== VideoControlStates.ControlButtons &&
        section.ref !== VideoControlStates.Ranges)
    ) {
      return
    }
    this.restartControlsTimer()
    this._setState(section.ref)
  }

  scrubFromClick(scrubTo, automaticallyResume = false) {
    this._setState(VideoControlStates.Ranges)
    this.scrubFromClick(scrubTo, automaticallyResume)
  }

  //We can't attach the mousemove event to the progress bar directly as this event is not supported by Lightning, hence we use coordinates instead
  scrubFromHover(scrubTo, clientY) {
    if (
      this._isScrubMode &&
      clientY >= this.innerContainer.y - this.ranges.progressBar.finalH &&
      clientY <= this.innerContainer.y
    ) {
      this.scrubFromClick(scrubTo)
    }
  }

  // This handler specifically deals with the UpNextCarousel
  checkNavigationPointersVisibility(shouldShow) {
    const { isCursorVisible } = AppConfig.shared.appSettings
    const carousel = this.upNextCarousel?.carouselsContainer?.childList?.getAt(0)

    if (carousel?.navigationPointers) {
      // Specific position overrides for this carousel on the player, as it's not aligned with the rest of the screens
      carousel.navigationPointers.w = SCREEN_WIDTH - this.upNextCarouselContainer.x
      carousel.navigationPointers.leftContainer.x =
        HorizontalPointerWidth.RIGHT - HorizontalPointerWidth.LEFT - this.upNextCarouselContainer.x // -5

      carousel.navigationPointers.visible = shouldShow && isCursorVisible
      carousel.checkNavigationPointersVisibility()
    }
  }
}
